import { dehydrate, QueryClient } from "@tanstack/react-query";
import { NextSeo } from "next-seo";
import { useEffect, useRef, ReactElement } from "react";
import ContainerBoundary from "@/components/common/ContainerBoundary";
import AuthorContainer from "@/components/home/author/AuthorContainer";
import Hero from "@/components/home/hero/Hero";
import SportSection from "@/components/home/sport/SportSection";
import { HomeLayout } from "@/components/layout/Layout";
import { HOME_PAGE_VIEW } from "@/constants/amplitude";
import { Template } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useHomepage from "@/utils/hooks/useHomepage";
import logger from "@/utils/logger";
import { SEO } from "../constants/index";
import { loadHeader, loadSports, loadHomepage } from "../utils/ssr";
import type { LayoutProps } from "@/components/layout/Layout";
import styles from "./index.module.scss";

const Home = () => {
  const titleRef = useRef("");
  const { setPage, logAmplitudeEvent, isActive } = useAmplitude();
  const { data: homepageData } = useHomepage();
  const articles = homepageData?.articles ?? [];
  const selectedSports = homepageData?.selectedSports ?? [];
  const tools = homepageData?.tools ?? [];
  const template = homepageData?.template ?? Template.Template1;

  useEffect(() => {
    if (titleRef.current !== SEO.RESEARCH_PAGE_TITLE && isActive) {
      titleRef.current = SEO.RESEARCH_PAGE_TITLE;
      setPage("home");
      logAmplitudeEvent(HOME_PAGE_VIEW, {
        "Page Title": SEO.RESEARCH_PAGE_TITLE,
        "Page Name": "home",
      });
    }
  }, [setPage, logAmplitudeEvent, isActive]);

  return (
    <>
      <NextSeo
        title={SEO.RESEARCH_PAGE_TITLE}
        description={SEO.SEO_DESCRIPTION}
        canonical={SEO.FANDUEL_URL_RESEARCH}
      />
      <main className={styles.mainContent}>
        <div className={styles.heroContainer}>
          <Hero articles={articles} template={template} tools={tools} />
        </div>
        <div className={styles.boundaryContainer}>
          <ContainerBoundary>
            <div className={styles.conditionalColsContainer}>
              <div className={styles.conditionalCols}>
                <div className={styles.sectionContainer}>
                  {selectedSports.length ? (
                    <SportSection selectedSports={selectedSports} />
                  ) : undefined}
                </div>
              </div>
            </div>
            <div className={styles.sectionContainer}>
              <AuthorContainer />
            </div>
          </ContainerBoundary>
        </div>
      </main>
    </>
  );
};

export default Home;

export async function getServerSideProps() {
  try {
    const queryClient = new QueryClient();
    await Promise.all([
      loadHeader(queryClient),
      loadHomepage(queryClient),
      loadSports(queryClient),
    ]);
    return {
      props: {
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (err) {
    logger.error("Error: homepage", err);
    return {
      notFound: true,
    };
  }
}

Home.getLayout = (page: ReactElement) => (
  <HomeLayout {...(page.props as Partial<LayoutProps>)}>{page}</HomeLayout>
);
